import React, { useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Dialog, DialogContent, DialogActions, Button } from "@mui/material";
import { toast } from "react-toastify";
import { upload_file_to_s3 } from "../../utils";
import { serviceUrl, genericHeaders } from "../../utils/apiConfig";

const ReuploadStatusReportModal = ({ open, onClose, sheet, onSuccess }) => {
  const [fileData, setFileData] = useState({ fileName: "", file: null });
  const [s3Document, setS3Document] = useState(null);
  const [billingHours, setBillingHours] = useState(sheet?.billingHours ?? "");
  const [description, setDescription] = useState(sheet?.description ?? "");
  const [docLoader, setDocLoader] = useState(false);
  const [loading, setLoading] = useState(false);

  const billingPeriod =
    sheet?.billingPeriodMonth && sheet?.billingPeriodYear
      ? dayjs()
          .month(sheet.billingPeriodMonth - 1)
          .year(sheet.billingPeriodYear)
          .format("MMMM YYYY")
      : "—";

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSizeInBytes = 10 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      toast.error("File size limit is 10MB");
      return;
    }

    setFileData({ fileName: file.name, file });
    setS3Document(null);
    setDocLoader(true);
    try {
      const s3path = await upload_file_to_s3(file);
      const s3Data = s3path?.data?.data;
      if (!s3Data?.fileName) {
        toast.error("File upload failed. Please try again.");
        setFileData({ fileName: "", file: null });
        return;
      }
      setS3Document({ ...s3Data, originalFileName: file.name });
    } catch (err) {
      toast.error("File upload failed. Please try again.");
      setFileData({ fileName: "", file: null });
      console.error(err);
    } finally {
      setDocLoader(false);
    }
  };

  const handleSubmit = async () => {
    const payload = {
      billingHours: Number(billingHours),
      billingPeriodMonth: sheet?.billingPeriodMonth,
      billingPeriodYear: sheet?.billingPeriodYear,
      description,
      statusReport: {
        fileName: s3Document?.fileName,
        folderPath: s3Document?.folderPath,
        timeStamp: s3Document?.timeStamp,
        bucketName: s3Document?.bucketName,
        originalFileName: s3Document?.originalFileName,
        region: s3Document?.region,
      },
    };

    setLoading(true);
    try {
      const response = await axios.post(
        `${serviceUrl}/api/v2/statusReport/upload`,
        payload,
        { headers: genericHeaders() }
      );
      if (response.status === 200 || response.status === 201) {
        toast.success(response.data?.message || "Status report re-uploaded successfully.");
        handleClose();
        onSuccess();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to re-upload status report. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFileData({ fileName: "", file: null });
    setS3Document(null);
    setBillingHours(sheet?.billingHours ?? "");
    setDescription(sheet?.description ?? "");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogContent className="space-y-5">
        <h2 className="text-xl font-semibold text-gray-800">
          Re-upload Status Report
        </h2>

        {/* Billing period — read only */}
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-600 min-w-[160px]">
            Month
          </span>
          <span className="text-sm font-semibold text-gray-800">
            {billingPeriod}
          </span>
        </div>

        {/* File upload */}
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-600 min-w-[160px]">
            Status Report File <span className="text-red-500">*</span>
          </span>
          <div className="relative w-60">
            <input
              type="file"
              onChange={handleFileUpload}
              disabled={docLoader}
              className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10 disabled:cursor-not-allowed"
            />
            <div className="w-60 h-[38px] px-3 border border-gray-300 rounded-md shadow-sm text-sm flex items-center justify-between bg-white">
              <span
                className={`truncate ${
                  fileData.fileName ? "text-gray-700" : "text-gray-400"
                }`}
              >
                {fileData.fileName || "Choose a file..."}
              </span>
              {docLoader ? (
                <div className="border-2 border-t-blue-500 border-gray-200 rounded-full w-4 h-4 animate-spin flex-shrink-0" />
              ) : (
                <span className="text-blue-500 text-xs flex-shrink-0 ml-1">
                  Browse
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Billing hours */}
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-600 min-w-[160px]">
            Effective Working Hours <span className="text-red-500">*</span>
          </span>
          <input
            type="number"
            value={billingHours}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || (Number(val) >= 0 && Number(val) <= 744)) {
                setBillingHours(val);
              }
            }}
            placeholder="Write in hours"
            className="w-60 h-[38px] border border-gray-300 rounded-md px-3 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Description */}
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-gray-600">Description <span className="text-red-500">*</span></span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter description"
            rows={3}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
          />
        </div>
      </DialogContent>

      <DialogActions className="px-6 pb-4">
        <Button onClick={handleClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!s3Document || !billingHours || !description.trim() || docLoader || loading}
        >
          {loading ? "Submitting..." : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReuploadStatusReportModal;
